import {
    DateFormats,
    formatDate,
    formatGender,
    formatPhoneNumber,
    getAge,
} from '@strata/helper-functions';
import { EnrollmentCoverageDates } from '@strata/react-enrollment-dates';
import { FormSectionWithRows } from '@strata/react-form-section';
import { MailToHyperlink } from '@strata/react-hyperlink';
import {
    AddressDetailLabelValue,
    DetailLabel,
    DetailLabelList,
    DetailLabelValuePair,
} from '@strata/react-label-value-pairs';
import { InformationIcon } from '@strata/react-status-icons';
import { StatusIndicator } from '@strata/react-status-indicators';
import { useFlags } from 'launchdarkly-react-client-sdk';

import {
    type EligibilityDisplay,
    getMemberEligibilityStatusDisplay,
    PLATFORM_ELIGIBILITY_PENDING,
} from 'services/memberEligibilityStatus';
import { type MemberDetails } from 'store/api/scopeAssessorApi';

interface TreatmentMemberDetailsProps {
    memberDetails?: MemberDetails;
    willBeUnmanagedOn?: string;
}

const EnrollmentDetails = ({
    memberDetails = undefined,
    willBeUnmanagedOn = undefined,
    eligibilityDisplay,
}: TreatmentMemberDetailsProps & { eligibilityDisplay: EligibilityDisplay }) => {
    const showWillBeUnmanagedOn =
        memberDetails?.enrollmentStatus === PLATFORM_ELIGIBILITY_PENDING && !!willBeUnmanagedOn;

    return (
        <div style={{ width: '100%' }}>
            <div className="padding__bottom--x-large" style={{ display: 'flex', width: '100%' }}>
                <div style={{ width: '50%' }}>
                    <DetailLabel label="Enrollment Status" />
                    <StatusIndicator
                        message={eligibilityDisplay.label}
                        status={eligibilityDisplay.status}
                        tooltipText={eligibilityDisplay.tooltipText}
                    />
                </div>
                <DetailLabelValuePair
                    style={{ width: '50%' }}
                    label="Enrollment change date"
                    value={formatDate(memberDetails?.lastUpdated, DateFormats.DATE_TIME)}
                />
            </div>
            <div style={{ display: 'flex', width: '100%' }}>
                {showWillBeUnmanagedOn ? (
                    <DetailLabelValuePair
                        style={{ width: '50%' }}
                        label="Grace period ends"
                        value={formatDate(willBeUnmanagedOn)}
                    />
                ) : null}
                <DetailLabelValuePair
                    style={{ width: '50%' }}
                    label="Coverage dates"
                    value={
                        <EnrollmentCoverageDates
                            enrollmentStart={memberDetails?.enrollmentStart}
                            enrollmentEnd={memberDetails?.enrollmentEnd}
                            isFmhEnrollmentEnd={memberDetails?.isFmhEnrollmentEnd}
                        />
                    }
                />
            </div>
        </div>
    );
};

export const TreatmentMemberDetails = ({
    memberDetails = undefined,
    willBeUnmanagedOn = undefined,
    // eslint-disable-next-line complexity
}: TreatmentMemberDetailsProps) => {
    const { payersEnhancedEligibility } = useFlags<{ payersEnhancedEligibility: boolean }>();
    const eligibilityDisplay = memberDetails
        ? getMemberEligibilityStatusDisplay(memberDetails.enrollmentStatus)
        : null;

    return (
        <FormSectionWithRows
            sectionTitle="Member Details"
            isLoading={!memberDetails}
            hoverOverlayText="Member details are hidden. Hover to view information."
            rowOneContent={
                <>
                    <DetailLabelValuePair
                        style={{ width: '50%' }}
                        label="Gender"
                        value={formatGender(memberDetails?.gender)}
                    />
                    <DetailLabelValuePair
                        style={{ width: '50%' }}
                        label="DOB"
                        value={formatDate(memberDetails?.dateOfBirth)}
                    />
                </>
            }
            rowTwoContent={
                <>
                    <DetailLabelValuePair
                        style={{ width: '50%' }}
                        label="Age"
                        value={getAge(memberDetails?.dateOfBirth)}
                    />
                    <AddressDetailLabelValue
                        style={{ width: '50%' }}
                        address1={memberDetails?.addressLine1}
                        address2={memberDetails?.addressLine2}
                        city={memberDetails?.city}
                        state={memberDetails?.state}
                        zip={memberDetails?.zip}
                    />
                </>
            }
            rowThreeContent={
                <>
                    <DetailLabelList
                        label="Phone"
                        style={{ width: '50%' }}
                        values={[
                            formatPhoneNumber(memberDetails?.phone),
                            ...(memberDetails?.secondaryPhone
                                ? [
                                      // eslint-disable-next-line react/jsx-key
                                      <div
                                          className="flex__gap--x-small"
                                          style={{ alignItems: 'center' }}
                                      >
                                          <span>
                                              {formatPhoneNumber(memberDetails.secondaryPhone)}
                                          </span>
                                          <InformationIcon tooltipText="Payer-submitted supplementary phone number" />
                                      </div>,
                                  ]
                                : []),
                        ]}
                    />
                    <DetailLabelValuePair
                        style={{ width: '50%' }}
                        label="Email"
                        value={
                            memberDetails?.email ? (
                                <MailToHyperlink emailAddress={memberDetails?.email} />
                            ) : undefined
                        }
                    />
                </>
            }
            rowFourContent={
                <>
                    <DetailLabelValuePair
                        style={{ width: '50%' }}
                        label="Group ID"
                        value={memberDetails?.benefitGroup}
                    />
                    <DetailLabelValuePair
                        style={{ width: '50%' }}
                        label="Group Name"
                        value={memberDetails?.benefitGroupName}
                    />
                </>
            }
            rowFiveContent={
                <>
                    <DetailLabelValuePair
                        style={{ width: '50%' }}
                        label="Carrier"
                        value={memberDetails?.carrier}
                    />
                    <DetailLabelValuePair
                        style={{ width: '50%' }}
                        label="Account"
                        value={memberDetails?.account}
                    />
                </>
            }
            rowSixContent={
                payersEnhancedEligibility && eligibilityDisplay ? (
                    <EnrollmentDetails
                        memberDetails={memberDetails}
                        willBeUnmanagedOn={willBeUnmanagedOn}
                        eligibilityDisplay={eligibilityDisplay}
                    />
                ) : undefined
            }
        />
    );
};
